import type { SettingsCategory, SettingsSubcategory } from '../models/settings-hub.model';

/** A card as laid out: the ids of its settings, in the order they are shown */
type CardLayout = { id: string; title: string; fullWidth?: boolean; settings: string[] };
type SubcategoryLayout = Omit<SettingsSubcategory, 'cards'> & { cards: CardLayout[] };
export type CategoryLayout = Omit<SettingsCategory, 'subcategories'> & { subcategories: SubcategoryLayout[] };

/**
 * What the page shows and where: groups, sections and cards follow the titles of the legacy settings page (its tabs, its cards and
 * their fieldsets), with the settings in the order they have there. The exceptions agreed with the product owner are the automatic
 * print (Integrations > Automations) and the switches of the interface (Advanced > Interface).
 * To add a setting, define it in its registry and list its id here.
 */
export const SETTINGS_LAYOUT: CategoryLayout[] = [
    {
        id: 'informazioni-negozio',
        title: 'SETTINGS.HUB.MENU.AREAS.INFORMAZIONI_NEGOZIO',
        subcategories: [
            {
                id: 'anagrafica',
                title: 'SETTINGS.HUB.MENU.SUBAREAS.ANAGRAFICA',
                icon: 'badge',
                cards: [
                    { id: 'dati-attivita', title: 'SETTINGS.HUB.MENU.CARDS.DATI_ATTIVITA', settings: ['general.shopname', 'general.company_name', 'general.vat_code', 'open-printer-logo-editor'] },
                    { id: 'indirizzo', title: 'SETTINGS.HUB.MENU.CARDS.INDIRIZZO', settings: ['general.address_street', 'general.address_zip', 'general.address_city', 'general.address_prov', 'general.address_country'] }
                ]
            },
            {
                id: 'localizzazione',
                title: 'SETTINGS.HUB.MENU.SUBAREAS.LOCALIZZAZIONE',
                icon: 'public',
                cards: [
                    { id: 'data-e-ora', title: 'SETTINGS.HUB.MENU.CARDS.DATA_E_ORA', settings: ['day_start_time_24', 'general.timezone'] },
                    { id: 'impostazioni-regionali', title: 'SETTINGS.HUB.MENU.CARDS.IMPOSTAZIONI_REGIONALI', settings: ['general.ui_language', 'general.currency'] }
                ]
            },
            {
                id: 'contatti',
                title: 'SETTINGS.HUB.MENU.SUBAREAS.CONTATTI',
                icon: 'contacts',
                cards: [
                    { id: 'contatti-principali', title: 'SETTINGS.HUB.MENU.CARDS.CONTATTI_PRINCIPALI', settings: ['general.email', 'general.bcc_copy', 'general.phone'] },
                    { id: 'social', title: 'SETTINGS.HUB.MENU.CARDS.SOCIAL', settings: ['general.whatsapp', 'general.telegram', 'general.messenger', 'general.wechat', 'general.snapchat', 'general.tiktok', 'general.instagram', 'general.facebook', 'general.twitter'] },
                    { id: 'presenza-online', title: 'SETTINGS.HUB.MENU.CARDS.PRESENZA_ONLINE', settings: ['general.website', 'general.map'] }
                ]
            }
        ]
    },
    {
        id: 'fatturazione-elettronica',
        title: 'SETTINGS.HUB.MENU.AREAS.FATTURAZIONE_ELETTRONICA',
        subcategories: [
            {
                id: 'configurazione',
                title: 'SETTINGS.HUB.MENU.SUBAREAS.CONFIGURAZIONE',
                icon: 'tune',
                cards: [
                    { id: 'parametri-invio', title: 'SETTINGS.HUB.MENU.CARDS.PARAMETRI_INVIO', settings: ['e_invoice.invoice_prefix', 'e_invoice.merchant.vat_subcode', 'open-einvoice-setup'] }
                ]
            },
            {
                id: 'dati-aziendali',
                title: 'SETTINGS.HUB.MENU.SUBAREAS.DATI_AZIENDALI',
                icon: 'business',
                cards: [
                    { id: 'anagrafica', title: 'SETTINGS.HUB.MENU.CARDS.ANAGRAFICA', settings: ['e_invoice.merchant.company_name', 'e_invoice.merchant.first_name', 'e_invoice.merchant.last_name', 'e_invoice.merchant.country', 'e_invoice.merchant.vat_code', 'search-vat-merchant', 'e_invoice.merchant.tax_code', 'e_invoice.merchant.tax_regime'] },
                    { id: 'sede', title: 'SETTINGS.HUB.MENU.CARDS.SEDE', settings: ['e_invoice.merchant.head_office.street', 'e_invoice.merchant.head_office.number', 'e_invoice.merchant.head_office.zip', 'e_invoice.merchant.head_office.city', 'e_invoice.merchant.head_office.prov', 'e_invoice.merchant.head_office.country'] },
                    { id: 'contatti', title: 'SETTINGS.HUB.MENU.CARDS.CONTATTI', settings: ['e_invoice.merchant.phone', 'e_invoice.merchant.fax', 'e_invoice.merchant.email', 'e_invoice.merchant.email_pec'] },
                    { id: 'coordinate-bancarie', title: 'SETTINGS.HUB.MENU.CARDS.COORDINATE_BANCARIE', settings: ['e_invoice.bank_merchant_name', 'e_invoice.bank_name', 'e_invoice.bank_iban', 'e_invoice.bic'] }
                ]
            }
        ]
    },
    {
        id: 'integrazioni',
        title: 'SETTINGS.HUB.CATEGORIES.INTEGRATIONS',
        subcategories: [
            {
                id: 'hospitality',
                title: 'SETTINGS.HUB.MENU.SUBAREAS.HOSPITALITY',
                icon: 'hotel',
                cards: [
                    { id: 'pms-zucchetti', title: 'SETTINGS.HUB.MENU.CARDS.PMS_ZUCCHETTI', settings: ['zucchetti_pms.send_other_sales'] },
                    { id: 'pms-lean', title: 'SETTINGS.HUB.MENU.CARDS.PMS_LEAN', settings: ['lean_pms.hide_room_prefix', 'lean_pms.group_charges_by_lean_product_code', 'lean_pms.cache_expiration_time', 'lean_pms.notes_category', 'lean_pms.sale_id_prefix', 'lean_pms.send_prefix_only', 'lean_pms.enable_daily_closing', 'lean_pms.advanced_payment_mapping_enable', 'lean_pms.advanced_payment_mapping', 'lean_pms.sync_customers', 'lean_pms.send_extra_sale', 'lean_pms.ask_room_number_on_sale_open', 'lean_pms.webhooks.bus', 'lean_pms.webhooks.extra_id', 'lean_pms.webhooks.hotel_id', 'reattach-lean-webhook', 'disconnect-lean', 'lean_pms.cash_payment_type_id', 'lean_pms.card_payment_type_id', 'lean_pms.cash_difference_payment_type_id'] },
                    { id: 'pienissimo', title: 'SETTINGS.HUB.MENU.CARDS.PIENISSIMO', settings: ['pienissimo.token', 'pienissimo.days_to_check', 'pienissimo.url', 'pienissimo.url_takeaway', 'pienissimo.url_delivery', 'pienissimo.manage_fidelities', 'pienissimo.giftcard_method', 'activate-pienissimo-sync', 'deactivate-pienissimo-sync', 'sync-pienissimo-products', 'sync-pienissimo-tables'] }
                ]
            },
            {
                id: 'marketing',
                title: 'SETTINGS.HUB.MENU.SUBAREAS.MARKETING',
                icon: 'campaign',
                cards: [
                    { id: '4dem', title: 'SETTINGS.HUB.MENU.CARDS.4DEM', settings: ['fdem_sync', 'force-four-dem-sync', 'fdem_apikey', 'fdem_host'] },
                    { id: 'selfordering', title: 'SETTINGS.HUB.MENU.CARDS.SELFORDERING', settings: ['orders.strooka_shared_key', 'orders.strooka_header_text', 'orders.strooka_api_url'] }
                ]
            },
            {
                id: 'retail-e-gestionali',
                title: 'SETTINGS.HUB.MENU.SUBAREAS.RETAIL_E_GESTIONALI',
                icon: 'storefront',
                cards: [
                    { id: 'integrazione-uve2k', title: 'SETTINGS.HUB.MENU.CARDS.INTEGRAZIONE_UVE2K', settings: ['uve.enabled', 'uve.user', 'uve.password', 'uve.appClientId', 'uve.url', 'uve.idCompany', 'uve.idPagamento', 'uve.area', 'uve.lotto'] },
                    { id: 'invio-corrispettivi-ai-gestionali-attraverso-il-digitalhub', title: 'SETTINGS.HUB.MENU.CARDS.INVIO_CORRISPETTIVI_AI_GESTIONALI_ATTRAVERSO_IL_DIGITALHUB', settings: ['daily_closing.digitalhub.enable', 'daily_closing.digitalhub.use_e_invoice_credentials', 'open-digitalhub-credentials', 'daily_closing.digitalhub.from_sw', 'daily_closing.digitalhub.to_sw', 'daily_closing.digitalhub.enable_riepilogo_zcoge_categories', 'daily_closing.digitalhub.enable_sedepdv', 'daily_closing.digitalhub.sedepdv'] },
                    { id: 'retailforce', title: 'SETTINGS.HUB.MENU.CARDS.RETAILFORCE', settings: ['retailforce.identification', 'retailforce.store_number', 'retailforce.cloud_api_key', 'retailforce.cloud_api_secret', 'retailforce.qr_code_size', 'retailforce.local', 'init-retailforce-auto-close'] },
                    { id: 'mago', title: 'SETTINGS.MAGO.TITLE', settings: ['disconnect-mago-orders'] }
                ]
            },
            {
                id: 'automazioni',
                title: 'SETTINGS.HUB.SUBCATEGORIES.AUTOMATIONS',
                icon: 'smart_toy',
                cards: [
                    { id: 'stampa-automatica', title: 'SETTINGS.HUB.CARDS.AUTO_PRINT', settings: ['automations.auto_print.enable', 'print-external-orders', 'print-external-sales', 'cashregister.batch_sale_print.default_printer_id', 'cashregister.batch_sale_print.default_document_id', 'cashregister.batch_sale_print.check_interval'] }
                ]
            }
        ]
    },
    {
        id: 'prodotti-e-magazzino',
        title: 'SETTINGS.HUB.MENU.AREAS.PRODOTTI_E_MAGAZZINO',
        subcategories: [
            {
                id: 'catalogo',
                title: 'SETTINGS.HUB.MENU.SUBAREAS.CATALOGO',
                icon: 'layers',
                cards: [
                    { id: 'opzioni-prodotti', title: 'SETTINGS.HUB.MENU.CARDS.OPZIONI_PRODOTTI', settings: ['items.enable_alternative_translate', 'items.option1_name', 'items.option2_name', 'items.option3_name', 'items.option4_name', 'refresh-items-cache'] },
                    { id: 'listino-prezzi', title: 'SETTINGS.HUB.MENU.CARDS.LISTINO_PREZZI', settings: ['price_list_1_name', 'price_list_1_hide', 'price_list_2_name', 'price_list_2_hide', 'price_list_3_name', 'price_list_3_hide', 'price_list_4_name', 'price_list_4_hide', 'price_list_5_name', 'price_list_5_hide', 'price_list_6_name', 'price_list_6_hide', 'price_list_7_name', 'price_list_7_hide', 'price_list_8_name', 'price_list_8_hide', 'price_list_9_name', 'price_list_9_hide', 'price_list_10_name', 'price_list_10_hide', 'orders.normal_pricelist', 'orders.take_away_pricelist', 'orders.delivery_pricelist'] }
                ]
            },
            {
                id: 'magazzino',
                title: 'SETTINGS.HUB.MENU.SUBAREAS.MAGAZZINO',
                icon: 'inventory_2',
                cards: [
                    { id: 'gestione-giacenze', title: 'SETTINGS.HUB.MENU.CARDS.GESTIONE_GIACENZE', settings: ['stock_movements.disable_item_cost_update', 'cashregister.disable_stock_notifications', 'cashregister.show_stock_on_showcase'] }
                ]
            }
        ]
    },
    {
        id: 'clienti',
        title: 'SETTINGS.HUB.MENU.AREAS.CLIENTI',
        subcategories: [
            {
                id: 'generale',
                title: 'SETTINGS.HUB.MENU.SUBAREAS.GENERALE',
                icon: 'group',
                cards: [
                    { id: 'opzioni', title: 'SETTINGS.HUB.MENU.CARDS.OPZIONI', settings: ['backend.mail_receipts', 'backend.enable_customer_welcome_mail', 'customers.custom_types', 'customers.verify_vat_tax_code'] },
                    { id: 'fidelity', title: 'SETTINGS.HUB.MENU.CARDS.FIDELITY', settings: ['cashregister.print_fidelity_points', 'cashregister.show_fidelity_points', 'fidelity_prefix', 'fidelity.current_message', 'fidelity.prevtotal_message', 'fidelity.total_message', 'fidelity.fidelity_label', 'digital_card.logo_url', 'digital_card.primary_color'] }
                ]
            },
            {
                id: 'registrazione',
                title: 'SETTINGS.HUB.MENU.SUBAREAS.REGISTRAZIONE',
                icon: 'how_to_reg',
                cards: [
                    { id: 'form-registrazione-pubblico', title: 'SETTINGS.HUB.MENU.CARDS.FORM_REGISTRAZIONE_PUBBLICO', settings: ['signup_form.enabled', 'open-signup-form-preview', 'signup_form.url_privacy', 'signup_form.thankyou_message', 'signup_form.verify_mobile', 'signup_form.verify_email', 'signup_form.return_digital_cards', 'signup_form.fields'] }
                ]
            }
        ]
    },
    {
        id: 'hardware-e-comunicazione',
        title: 'SETTINGS.HUB.MENU.AREAS.HARDWARE_E_COMUNICAZIONE',
        subcategories: [
            {
                id: 'comunicazione-locale',
                title: 'SETTINGS.HUB.MENU.SUBAREAS.COMUNICAZIONE_LOCALE',
                icon: 'hub',
                cards: [
                    { id: 'dispositivi-master', title: 'SETTINGS.HUB.MENU.CARDS.DISPOSITIVI_MASTER', settings: ['local_server.enable_android', 'local_server.enable_electron', 'local_server.enable_ios', 'local_server.port'] }
                ]
            },
            {
                id: 'display-cliente',
                title: 'SETTINGS.HUB.MENU.SUBAREAS.DISPLAY_CLIENTE',
                icon: 'dvr',
                cards: [
                    { id: 'configurazione-display', title: 'SETTINGS.HUB.MENU.CARDS.CONFIGURAZIONE_DISPLAY', settings: ['customer_display.enable_two_rows_display', 'customer_display.display_row_size', 'customer_display.serial_port_address', 'customer_display.enable_network_display', 'customer_display.network_display_ip_address', 'customer_display.enable_color_display'] },
                    { id: 'contenuti', title: 'SETTINGS.HUB.MENU.CARDS.CONTENUTI', settings: ['customer_display.greeting_message', 'customer_display.thankyou_message', 'customer_display.logo_url', 'customer_display.standby_url', 'customer_display.thankyou_message_timeout', 'customer_display.ask_customer_survey'] }
                ]
            },
            {
                id: 'bilance',
                title: 'SETTINGS.HUB.MENU.SUBAREAS.BILANCE',
                icon: 'scale',
                cards: [
                    { id: 'bilance-configura', title: 'SETTINGS.HUB.MENU.CARDS.BILANCE_CONFIGURA', settings: ['scales.list', 'cashregister.scales.legacy_piece_letters', 'cashregister.scales.manage_categories'] }
                ]
            }
        ]
    },
    {
        id: 'cassa-e-ordini',
        title: 'SETTINGS.HUB.MENU.AREAS.CASSA_E_ORDINI',
        subcategories: [
            {
                id: 'ordini',
                title: 'SETTINGS.HUB.MENU.SUBAREAS.ORDINI',
                icon: 'receipt_long',
                cards: [
                    { id: 'coperto', title: 'SETTINGS.HUB.MENU.CARDS.COPERTO', settings: ['orders.automated_add_cover', 'orders.automated_add_cover.type', 'orders.automated_add_cover.value', 'orders.automated_add_cover.item', 'cashregister.prebill.covers_info.enabled', 'cashregister.prebill.covers_info.per_cover_amount_label', 'orders.apply_cover_to_total'] },
                    { id: 'flusso-comande', title: 'SETTINGS.HUB.MENU.CARDS.FLUSSO_COMANDE', settings: ['orders.open_tables_after_park', 'orders.return_tables_takeaway_delivery', 'orders.open_tables_after_skip_send', 'orders.open_tables_after_send', 'cashregister.open_tables_after_send', 'orders.skip_creation_dialog', 'orders.drop_variations_on_park', 'cashregister.keep_table_open'] },
                    { id: 'gestione-comanda', title: 'SETTINGS.HUB.MENU.CARDS.GESTIONE_COMANDA', settings: ['orders.require_table_selection', 'cashregister.ask_seller_for_each_sale', 'tables.hanging_alarm_after', 'orders.disable_take_away_add_customer', 'orders.disable_delivery_add_customer', 'orders.allow_street_only_shipping_addresses', 'orders.skip_checkout_confirm', 'cashregister.ask_shipping_address', 'orders.add_item_as_new', 'orders.ingredients_removal_affects_price', 'orders.ingredients_removal_disabled', 'cashregister.disable_menu_ingredients_pricing'] },
                    { id: 'stampa-comande', title: 'SETTINGS.HUB.MENU.CARDS.STAMPA_COMANDE', settings: ['cashregister.print_orders_on_sale_close', 'cashregister.enable_print_order', 'cashregister.print_order_header_variations', 'orders.exits', 'orders.exits_names', 'orders.default_exit', 'orders.print_attempts', 'cashregister.print_nonfiscal_sale_on_store', 'orders.print_replacement_order_for_delivery_takeaway'] }
                ]
            },
            {
                id: 'vendite',
                title: 'SETTINGS.HUB.MENU.SUBAREAS.VENDITE',
                icon: 'point_of_sale',
                cards: [
                    { id: 'processi-di-vendita', title: 'SETTINGS.HUB.MENU.CARDS.PROCESSI_DI_VENDITA', settings: ['general.simplified_accessibility', 'cashregister.hide_zero_price_warnings', 'cashregister.skip_archive_confirmation', 'cashregister.open_cashdrawer_on_store', 'cashregister.register_cashdrawers_paid_amount', 'cashregister.disable_cash_limit_exceeded_confirm', 'printers.verify_cash_on_dailyclousure', 'cashregister.disable_customer_invoice_prompt', 'cashregister.enable_payment_rounding', 'cashregister.lock_sale_after_prebill', 'cashregister.require_buzzer_input', 'cashregister.payments.force_prepaid_credit', 'cashregister.enable_split_sale', 'cashregister.split_sale.split_items_on_dutch_treat', 'cashregister.split_sale.default_department_id', 'cashregister.disable_split_sale_amount_covers', 'verifone.enable_extended_receipt', 'cashregister.daily_report_per_seller', 'cashregister.table_name_on_topbar', 'cashregister.deselect_sales_from_menu_bar', 'cashregister.enable_research_product_by_3_chars', 'cashregister.request_prebill_from_stored', 'cashregister.return_to_history_after_summary_emission', 'history.show_stored_sales'] },
                    { id: 'gestione-articoli', title: 'SETTINGS.HUB.MENU.CARDS.GESTIONE_ARTICOLI', settings: ['cashregister.add_item_as_new', 'cashregister.check_stock', 'cashregister.count_nostock_as_unavailable', 'cashregister.disable_required_variations', 'cashregister.disable_manual_production_confirm', 'cashregister.remove_zero_price_items_from_receipt', 'orders.half_portion_enable', 'orders.half_portion_discount_value', 'showcase_hide_empty_categories', 'cashregister.enable_switch_price_list', 'cashregister.reset_pricelist_on_close'] },
                    { id: 'lettura-barcode', title: 'SETTINGS.HUB.MENU.CARDS.LETTURA_BARCODE', settings: ['cashregister.add_item_on_barcode_not_found', 'cashregister.error_sound_on_barcode_not_found', 'cashregister.dynamic_scales_barcode', 'cashregister.on_taxcode_not_found_show_add_customer'] },
                    { id: 'quantita-e-tastierino', title: 'SETTINGS.HUB.MENU.CARDS.QUANTITA_E_TASTIERINO', settings: ['cashregister.keyboard.integer_multiply', 'cashregister.keyboard.set_quantity', 'cashregister.keyboard.quantity_is_integer', 'cashregister.dynamic_item_price_check'] },
                    { id: 'crediti-buoni-e-mance', title: 'SETTINGS.HUB.MENU.CARDS.CREDITI_BUONI_E_MANCE', settings: ['prepaid.recharge_department_id', 'cashregister.send_giftcards_as_unclaimed_payments', 'cashregister.tip.enable', 'cashregister.tip.department', 'cashregister.tip.predefined_values', 'cashregister.tip.reference_text', 'cashregister.expense_report.template_id', 'cashregister.expense_report.department_id', 'cashregister.quick_coupons_list'] },
                    { id: 'stampe-e-documenti', title: 'SETTINGS.HUB.MENU.CARDS.STAMPE_E_DOCUMENTI', settings: ['cashregister.queue.mode', 'cashregister.queue.print_type', 'cashregister.queue.max_value', 'fiscalprinter.e_invoice_receipt_copies', 'nofiscalprinter.disable_nonfiscal_sale_header', 'cashregister.save_paper_on_prebill', 'cashregister.save_paper_on_e_invoice', 'cashregister.enable_receipt_options', 'cashregister.public_receipt', 'cashregister.public_receipt_duration'] }
                ]
            },
            {
                id: 'prenotazioni',
                title: 'SETTINGS.HUB.MENU.SUBAREAS.PRENOTAZIONI',
                icon: 'event',
                cards: [
                    { id: 'gestione-prenotazioni', title: 'SETTINGS.HUB.MENU.CARDS.GESTIONE_PRENOTAZIONI', settings: ['bookings.slot_duration', 'tables.booking_warning', 'disable_pos_booking_emails', 'disable_external_booking_emails'] }
                ]
            }
        ]
    },
    {
        id: 'notifiche-e-sicurezza',
        title: 'SETTINGS.HUB.MENU.AREAS.NOTIFICHE_E_SICUREZZA',
        subcategories: [
            {
                id: 'sicurezza',
                title: 'SETTINGS.HUB.MENU.SUBAREAS.SICUREZZA',
                icon: 'verified_user',
                cards: [
                    { id: 'accesso-operatori', title: 'SETTINGS.HUB.MENU.CARDS.ACCESSO_OPERATORI', settings: ['users.enable_user_logout', 'users.enable_user_switch', 'users.ask_the_operator_open_app', 'users.enable_user_logout_pin', 'users.enable_user_login_from_badge'] }
                ]
            },
            {
                id: 'notifiche',
                title: 'SETTINGS.HUB.MENU.SUBAREAS.NOTIFICHE',
                icon: 'notifications',
                cards: [
                    { id: 'notifiche-operative', title: 'SETTINGS.HUB.MENU.CARDS.NOTIFICHE_OPERATIVE', settings: ['cashregister.disable_external_sales_notification', 'dashboard.disable_einvoice_status', 'cashregister.disable_external_bookings_notification', 'e_invoice.notify_errors', 'dashboard.einvoice_quantity_warning'] }
                ]
            }
        ]
    },
    {
        id: 'pagamenti',
        title: 'SETTINGS.HUB.MENU.AREAS.PAGAMENTI',
        subcategories: [
            {
                id: 'pagamenti-rapidi',
                title: 'SETTINGS.HUB.MENU.SUBAREAS.PAGAMENTI_RAPIDI',
                icon: 'bolt',
                cards: [
                    { id: 'pulsanti-rapidi', title: 'SETTINGS.HUB.MENU.CARDS.PULSANTI_RAPIDI', settings: ['cashregister.fast_payment_1_type', 'cashregister.fast_payment_1_label', 'cashregister.fast_payment_2_type', 'cashregister.fast_payment_2_label', 'cashregister.fast_payment_3_type', 'cashregister.fast_payment_3_label'] }
                ]
            },
            {
                id: 'metodi-di-pagamento',
                title: 'SETTINGS.HUB.MENU.SUBAREAS.METODI_DI_PAGAMENTO',
                icon: 'payments',
                cards: [
                    { id: 'metodi-di-pagamento-card', title: 'SETTINGS.HUB.MENU.CARDS.METODI_DI_PAGAMENTO', fullWidth: true, settings: ['payment-methods'] }
                ]
            }
        ]
    },
    {
        id: 'avanzate',
        title: 'SETTINGS.HUB.CATEGORIES.ADVANCED',
        subcategories: [
            {
                id: 'interfaccia',
                title: 'SETTINGS.HUB.SUBCATEGORIES.INTERFACE',
                icon: 'science',
                cards: [
                    { id: 'aggiornamento-interfaccia', title: 'SETTINGS.SETTINGS.MIGRATIONS', settings: ['migration.bookings', 'migration.items', 'migration.history.e_invoices', 'migration.fidelity', 'migration.settings.printers', 'migration.stock.management', 'migration.stock.movements', 'migration.settings.general', 'migration.cashregister.ui'] }
                ]
            }
        ]
    }
];
